import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Search, Scale, Loader2, AlertCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function AdminGlossary({ user }) {
  const navigate = useNavigate();
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [formData, setFormData] = useState({ term: '', definition: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGlossary();
  }, []);

  const fetchGlossary = async () => {
    try {
      const response = await fetch(`${API}/glossary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGlossaryTerms(data);
      }
    } catch (error) {
      console.error('Error fetching glossary:', error);
      toast.error('Failed to load glossary');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const handleAddTerm = async () => {
    if (!formData.term.trim() || !formData.definition.trim()) {
      toast.error('Please fill in both term and definition');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/glossary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Term added successfully');
        setShowAddModal(false);
        setFormData({ term: '', definition: '' });
        fetchGlossary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add term');
      }
    } catch (error) {
      toast.error('Failed to add term');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTerm = async () => {
    if (!formData.term.trim() || !formData.definition.trim()) {
      toast.error('Please fill in both term and definition');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/glossary/${selectedTerm.term_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Term updated successfully');
        setShowEditModal(false);
        setSelectedTerm(null);
        setFormData({ term: '', definition: '' });
        fetchGlossary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update term');
      }
    } catch (error) {
      toast.error('Failed to update term');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTerm = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/glossary/${selectedTerm.term_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Term deleted successfully');
        setShowDeleteModal(false);
        setSelectedTerm(null);
        fetchGlossary();
      } else {
        toast.error('Failed to delete term');
      }
    } catch (error) {
      toast.error('Failed to delete term');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedGlossary = async () => {
    try {
      const response = await fetch(`${API}/admin/glossary/seed`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchGlossary();
      } else {
        toast.error(data.detail || data.message || 'Failed to seed glossary');
      }
    } catch (error) {
      toast.error('Failed to seed glossary');
    }
  };

  const openEditModal = (term) => {
    setSelectedTerm(term);
    setFormData({ term: term.term, definition: term.definition });
    setShowEditModal(true);
  };

  const openDeleteModal = (term) => {
    setSelectedTerm(term);
    setShowDeleteModal(true);
  };

  const filteredTerms = glossaryTerms.filter(term =>
    term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
    term.definition.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can manage the legal glossary.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-6">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-[#80298F]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Scale className="h-8 w-8 text-[#80298F]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Legal Glossary</h1>
              <p className="text-gray-500">Add, edit, or remove legal terms and definitions</p>
            </div>
          </div>
          <div className="flex gap-2">
            {glossaryTerms.length === 0 && (
              <Button
                variant="outline"
                onClick={handleSeedGlossary}
                className="text-[#80298F] border-[#80298F]"
              >
                Load Default Terms
              </Button>
            )}
            <Button
              onClick={() => {
                setFormData({ term: '', definition: '' });
                setShowAddModal(true);
              }}
              className="bg-[#80298F] hover:bg-[#6b2279]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Term
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search terms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Terms List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Legal Terms ({filteredTerms.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
              </div>
            ) : glossaryTerms.length === 0 ? (
              <div className="text-center py-12">
                <Scale className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No glossary terms yet</p>
                <p className="text-sm text-gray-400">Click "Load Default Terms" to populate with standard legal terms, or add your own.</p>
              </div>
            ) : filteredTerms.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No terms match "{searchQuery}"</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredTerms.map((term) => (
                  <div key={term.term_id} className="py-4 flex items-start justify-between gap-4 group hover:bg-gray-50 rounded-lg px-2 -mx-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#80298F] mb-1">{term.term}</h3>
                      <p className="text-sm text-gray-600">{term.definition}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(term)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-[#80298F] hover:bg-purple-50"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(term)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Term Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Term</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="term">Term</Label>
              <Input
                id="term"
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                placeholder="e.g., Deposition"
              />
            </div>
            <div>
              <Label htmlFor="definition">Definition</Label>
              <Textarea
                id="definition"
                value={formData.definition}
                onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
                placeholder="Enter a clear, client-friendly definition..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddTerm}
              disabled={saving}
              className="bg-[#80298F] hover:bg-[#6b2279]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Term
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Term Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Term</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-term">Term</Label>
              <Input
                id="edit-term"
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-definition">Definition</Label>
              <Textarea
                id="edit-definition"
                value={formData.definition}
                onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditTerm}
              disabled={saving}
              className="bg-[#80298F] hover:bg-[#6b2279]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Term</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete "<span className="font-semibold">{selectedTerm?.term}</span>"? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTerm}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminGlossary;
